import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const About: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    },
  };

  return (
    <section id="about" className="section-padding bg-dark-blue relative">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-deep-blue to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-deep-blue to-transparent"></div>
      <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-64 h-64 bg-electric-blue/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold mb-6">
            About <span className="gradient-text">Atomcx.ai</span>
          </motion.h2>
          
          <motion.p variants={itemVariants} className="text-lg mb-8 text-gray-300">
            At Atomcx.ai, we're on a mission to transform how businesses operate through the power of artificial intelligence. 
            Our team of AI experts and engineers develops cutting-edge intelligent agents that automate complex workflows, 
            making your business more efficient and competitive in today's digital landscape.
          </motion.p>
          
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <motion.div 
              variants={itemVariants}
              className="bg-deep-blue/50 p-6 rounded-lg border border-electric-blue/20 hover:shadow-neon transition-all"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-electric-blue to-teal-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-deep-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Innovation</h3>
              <p className="text-gray-400">
                We're constantly pushing the boundaries of what AI can do, developing new approaches to solve complex business challenges.
              </p>
            </motion.div>
            
            <motion.div 
              variants={itemVariants}
              className="bg-deep-blue/50 p-6 rounded-lg border border-electric-blue/20 hover:shadow-neon transition-all"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-electric-blue to-teal-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-deep-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Efficiency</h3>
              <p className="text-gray-400">
                Our AI agents streamline operations, reduce manual tasks, and optimize resource allocation, saving you time and money.
              </p>
            </motion.div>
            
            <motion.div 
              variants={itemVariants}
              className="bg-deep-blue/50 p-6 rounded-lg border border-electric-blue/20 hover:shadow-neon transition-all"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-electric-blue to-teal-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-deep-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Collaboration</h3>
              <p className="text-gray-400">
                Our AI agents work alongside your team, enhancing human capabilities rather than replacing them, creating a powerful synergy.
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;